import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import type { DashboardSummaryResponse, RecentPaymentDto, PaymentMethodBreakdown } from '../types/api-responses';

export type DateRangeKey = 'today' | 'alltime' | 'last7days' | 'last30days' | 'thismonth' | 'dateselected';

export interface DashboardSummary {
    totalAppointments: number;
    employees: number;
    activeBranches: number;
}

export interface BookingsRevenueData {
    labels: string[];
    bookings: number[];
    amounts: number[];
    totalRevenue: number;
    reservationRate: number;
}

export interface RevenueSeriesData {
    labels: string[];
    data: number[];
    year: number;
}

export interface AppointmentStatsData {
    labels: string[];
    values: number[];
}

interface UseDashboardOptions {
    date?: Date | null;
    range?: DateRangeKey;
}

const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
};

/**
 * O1 (AB#2199, SO-02) — the window goes on the wire as the owner's LOCAL calendar
 * dates ("yyyy-MM-dd"). Sending `startOfDay(local).toISOString()` made the backend
 * take the UTC date (`.Date`), which is the previous day for a UAE/Jordan owner, so the
 * window was shifted by a day.
 */
const toLocalDate = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const useDashboard = (opts?: UseDashboardOptions) => {
    const { user } = useUser();

    const [range, setRange] = useState<DateRangeKey>(opts?.range || 'last30days');
    const [selectedDate, setSelectedDate] = useState<Date | null>(opts?.date || null);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [summary, setSummary] = useState<DashboardSummary>({ totalAppointments: 0, employees: 0, activeBranches: 0 });
    const [bookingsRevenue, setBookingsRevenue] = useState<BookingsRevenueData>({ labels: [], bookings: [], amounts: [], totalRevenue: 0, reservationRate: 0 });
    const [revenueSeries, setRevenueSeries] = useState<RevenueSeriesData>({ labels: monthLabels, data: Array(12).fill(0), year: new Date().getFullYear() });
    const [appointmentStats, setAppointmentStats] = useState<AppointmentStatsData>({ labels: ['Pending', 'Confirm', 'Rejected', 'Completed'], values: [0, 0, 0, 0] });
    const [totalPaymentRevenue, setTotalPaymentRevenue] = useState<number>(0);
    const [recentPayments, setRecentPayments] = useState<RecentPaymentDto[]>([]);
    const [paymentMethodBreakdown, setPaymentMethodBreakdown] = useState<PaymentMethodBreakdown[]>([]);
    const [lastSuccessfulLoad, setLastSuccessfulLoad] = useState<number | null>(null);
    // O1 (AB#2199, SO-02) — only the latest request may write state, so a slow
    // earlier response (e.g. the first load racing a filter change) can never
    // overwrite newer figures.
    const requestSeqRef = useRef(0);

    const computeRange = useMemo(() => {
        const now = new Date();
        let start: Date | null = null;
        let end: Date | null = null;

        switch (range) {
            case 'today':
                start = startOfDay(now);
                end = endOfDay(now);
                break;
            case 'last7days':
                end = endOfDay(now);
                start = startOfDay(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000));
                break;
            case 'last30days':
                end = endOfDay(now);
                start = startOfDay(new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000));
                break;
            case 'thismonth':
                // AB#2206 — the 1st of the current month up to today.
                end = endOfDay(now);
                start = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
                break;
            case 'dateselected':
                if (selectedDate) {
                    start = startOfDay(selectedDate);
                    end = endOfDay(selectedDate);
                }
                break;
            case 'alltime':
            default:
                start = null;
                end = null;
                break;
        }
        return { start, end };
    }, [range, selectedDate]);

    const load = useCallback(async (): Promise<boolean> => {
        if (!user?.businessId) return false;
        const seq = ++requestSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const start = computeRange.start ? toLocalDate(computeRange.start) : undefined;
            const end = computeRange.end ? toLocalDate(computeRange.end) : undefined;
            const res: DashboardSummaryResponse = await apiService.getDashboardSummary(start, end);
            if (seq !== requestSeqRef.current) return false;

            setSummary({
                totalAppointments: res.totalAppointments,
                employees: res.employees,
                activeBranches: res.activeBranches
            });

            setBookingsRevenue({
                labels: res.bookingsRevenue.labels,
                bookings: res.bookingsRevenue.bookings,
                amounts: res.bookingsRevenue.amounts.map(Number),
                totalRevenue: Number(res.bookingsRevenue.totalRevenue),
                reservationRate: Number(res.bookingsRevenue.reservationRate)
            });

            setRevenueSeries({
                labels: res.revenueSeries.labels,
                data: res.revenueSeries.data.map(Number),
                year: res.revenueSeries.year
            });

            setAppointmentStats({
                labels: res.appointmentStats.labels,
                values: res.appointmentStats.values
            });

            setTotalPaymentRevenue(res.totalPaymentRevenue ?? Number(res.bookingsRevenue.totalRevenue));
            setRecentPayments(res.recentPayments ?? []);
            setPaymentMethodBreakdown(res.paymentMethodBreakdown ?? []);

            setLastSuccessfulLoad(Date.now());
            return true;
        } catch (e: unknown) {
            if (seq !== requestSeqRef.current) return false;
            const err = e as { message?: string };
            setError(err?.message || 'Failed to load dashboard');
            return false;
        } finally {
            if (seq === requestSeqRef.current) setLoading(false);
        }
    }, [user?.businessId, computeRange]);

    useEffect(() => {
        load();
    }, [load]);

    return {
        loading,
        error,
        range,
        setRange,
        selectedDate,
        setSelectedDate,
        summary,
        bookingsRevenue,
        revenueSeries,
        appointmentStats,
        totalPaymentRevenue,
        recentPayments,
        paymentMethodBreakdown,
        lastSuccessfulLoad,
        /** O1 — false until the first successful load: KPIs show a placeholder, never 0. */
        hasLoaded: lastSuccessfulLoad != null,
        refresh: load,
    } as const;
};
